import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Hammer, LogOut, LayoutDashboard, Inbox, FolderKanban, DollarSign, MessageSquareQuote,
  HelpCircle, Phone, Plus, Trash2, Save, X, Loader2, ExternalLink, TrendingUp,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/hooks/useSEO";
import { api, fileUrl } from "@/lib/api";
import { PROJECT_CATEGORIES } from "@/data/catalog";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Quoted", "Won", "Lost"];
const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  Quoted: "bg-purple-100 text-purple-700", Won: "bg-green-100 text-green-700", Lost: "bg-slate-200 text-slate-600",
};

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "enquiries", label: "Enquiries", icon: Inbox },
  { key: "projects", label: "Projects", icon: FolderKanban },
  { key: "price", label: "Price Guide", icon: DollarSign },
  { key: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { key: "faqs", label: "FAQs", icon: HelpCircle },
  { key: "contact", label: "Contact Info", icon: Phone },
];

export default function AdminDashboard() {
  useSEO({ title: "Admin Dashboard", description: "WAAN METAL admin portal.", noindex: true });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  if (!user) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-[#B87333]" size={32} /></div>;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-60 bg-[#0A1128] text-white flex flex-col shrink-0 fixed inset-y-0 hidden lg:flex">
        <div className="p-5 flex items-center gap-2.5 border-b border-white/10">
          <span className="flex items-center justify-center w-9 h-9 rounded-md bg-gradient-to-br from-[#B87333] to-[#D97706] text-[#0A1128]"><Hammer size={18} strokeWidth={2.5} /></span>
          <span className="font-display font-extrabold text-lg">WAAN<span className="text-[#D97706]">METAL</span></span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-[#B87333] text-[#0A1128]" : "text-slate-300 hover:bg-white/5"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5"><LogOut size={18} /> Logout</button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-60">
        <header className="bg-white border-b border-slate-200 px-5 lg:px-8 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-lg font-bold text-slate-900">{TABS.find((t) => t.key === tab)?.label}</h1>
            <p className="text-xs text-slate-500">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-2 lg:hidden">
            <select value={tab} onChange={(e) => setTab(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm">
              {TABS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <button onClick={logout} className="p-2 rounded-lg border border-slate-300"><LogOut size={18} /></button>
          </div>
        </header>

        <div className="p-5 lg:p-8">
          {tab === "overview" && <Overview />}
          {tab === "enquiries" && <Enquiries />}
          {tab === "projects" && <Projects />}
          {tab === "price" && <PriceGuide />}
          {tab === "testimonials" && <Testimonials />}
          {tab === "faqs" && <FAQs />}
          {tab === "contact" && <ContactInfo />}
        </div>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  const cards = [
    { label: "Total Enquiries", value: stats?.total_enquiries, icon: Inbox },
    { label: "New Enquiries", value: stats?.new_enquiries, icon: TrendingUp },
    { label: "Won", value: stats?.won_enquiries, icon: DollarSign },
    { label: "Conversion Rate", value: stats ? `${stats.conversion_rate}%` : null, icon: TrendingUp },
    { label: "Projects Published", value: stats?.projects, icon: FolderKanban },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-overview">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between"><span className="text-sm text-slate-500">{c.label}</span><c.icon size={20} className="text-[#B87333]" /></div>
          <p className="mt-3 text-3xl font-extrabold text-slate-900">{c.value ?? "—"}</p>
        </div>
      ))}
    </div>
  );
}

function Enquiries() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/enquiries").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/enquiries/${id}`, { status }); toast.success("Status updated"); load(); setOpen((o) => o && o.id === id ? { ...o, status } : o); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left min-w-[720px]" data-testid="admin-enquiries-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-5 py-3.5">Ref</th><th className="px-5 py-3.5">Name</th><th className="px-5 py-3.5">Type</th><th className="px-5 py-3.5">Date</th><th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50" data-testid={`admin-enquiry-${e.id}`}>
                <td className="px-5 py-3.5 font-mono-label text-sm text-slate-800">{e.reference}</td>
                <td className="px-5 py-3.5 text-slate-800">{e.name}<div className="text-xs text-slate-400">{e.email}</div></td>
                <td className="px-5 py-3.5 text-sm text-slate-600">{e.project_type}</td>
                <td className="px-5 py-3.5 text-sm text-slate-500">{new Date(e.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3.5"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[e.status] || ""}`}>{e.status}</span></td>
                <td className="px-5 py-3.5"><button onClick={() => setOpen(e)} data-testid={`admin-enquiry-view-${e.id}`} className="text-[#B87333] font-semibold text-sm">View</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No enquiries yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-enquiry-modal">
            <div className="flex items-start justify-between mb-4">
              <div><span className="font-mono-label text-sm text-[#B87333]">{open.reference}</span><h3 className="text-xl font-bold text-slate-900">{open.name}</h3></div>
              <button onClick={() => setOpen(null)}><X size={22} className="text-slate-400" /></button>
            </div>
            <div className="space-y-2.5 text-sm">
              <Row label="Project Type" value={open.project_type} />
              <Row label="Services" value={(open.services || []).join(", ") || "—"} />
              <Row label="Property" value={open.property_type} />
              <Row label="Floor Area" value={open.floor_area} />
              <Row label="Location" value={open.location} />
              <Row label="Budget" value={open.budget} />
              <Row label="Commencement" value={open.commencement_date} />
              <Row label="Description" value={open.description} />
              <Row label="Phone" value={open.phone} />
              <Row label="Email" value={open.email} />
              <Row label="Preferred Contact" value={open.preferred_contact} />
              {open.file_ids?.length > 0 && (
                <div><p className="text-slate-500 mb-1">Files ({open.file_ids.length})</p>
                  <div className="flex flex-wrap gap-2">{open.file_ids.map((f) => <a key={f} href={fileUrl(f)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 text-sm text-slate-700 hover:bg-slate-200"><ExternalLink size={13} /> File</a>)}</div>
                </div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t">
              <p className="text-sm font-medium text-slate-700 mb-2">Update status</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => <button key={s} onClick={() => setStatus(open.id, s)} data-testid={`admin-status-${s.toLowerCase()}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${open.status === s ? "ring-2 ring-[#B87333] " : ""}${STATUS_COLORS[s]}`}>{s}</button>)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => value ? <div className="flex gap-2"><span className="text-slate-500 w-32 shrink-0">{label}:</span><span className="text-slate-800">{value}</span></div> : null;

const inp = "w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none text-sm";
const lbl = "text-xs font-medium text-slate-600 block mb-1";

function CrudSection({ title, testid, endpoint, adminEndpoint, columns, emptyForm, renderForm, renderRow }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get(adminEndpoint).then(({ data }) => setItems(data)).catch(() => {}), [adminEndpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing.id && items.find((i) => i.id === editing.id)) await api.put(`${endpoint}/${editing.id}`, editing);
      else await api.post(endpoint, editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  return (
    <div data-testid={testid}>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...emptyForm })} data-testid={`${testid}-add`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold text-sm"><Plus size={16} /> Add</button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left min-w-[640px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{columns.map((c) => <th key={c} className="px-5 py-3.5">{c}</th>)}<th className="px-5 py-3.5"></th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((it) => (
              <tr key={it.id} className="hover:bg-slate-50" data-testid={`${testid}-row-${it.id}`}>
                {renderRow(it)}
                <td className="px-5 py-3.5 whitespace-nowrap text-right">
                  <button onClick={() => setEditing({ ...it })} className="text-[#B87333] font-semibold text-sm mr-3" data-testid={`${testid}-edit-${it.id}`}>Edit</button>
                  <button onClick={() => del(it.id)} className="text-red-500" data-testid={`${testid}-delete-${it.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-slate-500">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-modal`}>
            <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-bold text-slate-900">{editing.id && items.find((i) => i.id === editing.id) ? "Edit" : "Add"} {title}</h3><button onClick={() => setEditing(null)}><X size={22} className="text-slate-400" /></button></div>
            <div className="space-y-4">{renderForm(editing, (patch) => setEditing((e) => ({ ...e, ...patch })))}</div>
            <button onClick={save} data-testid={`${testid}-save`} className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold"><Save size={17} /> Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Projects() {
  return (
    <CrudSection
      title="Project" testid="admin-projects" endpoint="/projects" adminEndpoint="/admin/projects"
      columns={["Title", "Category", "Featured"]}
      emptyForm={{ title: "", category: "Residential", location: "", description: "", scope: "", image_url: "", completion_year: "", featured: false, published: true }}
      renderRow={(it) => (<>
        <td className="px-5 py-3.5 text-slate-800 font-medium">{it.title}</td>
        <td className="px-5 py-3.5 text-sm text-slate-600">{it.category}</td>
        <td className="px-5 py-3.5 text-sm">{it.featured ? "★" : "—"}</td>
      </>)}
      renderForm={(e, set) => (<>
        <div><label className={lbl}>Title</label><input className={inp} value={e.title} onChange={(ev) => set({ title: ev.target.value })} data-testid="admin-project-title" /></div>
        <div><label className={lbl}>Category</label><select className={inp} value={e.category} onChange={(ev) => set({ category: ev.target.value })} data-testid="admin-project-category">{PROJECT_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}</select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Location</label><input className={inp} value={e.location || ""} onChange={(ev) => set({ location: ev.target.value })} /></div>
          <div><label className={lbl}>Completion Year</label><input className={inp} value={e.completion_year || ""} onChange={(ev) => set({ completion_year: ev.target.value })} /></div>
        </div>
        <div><label className={lbl}>Image URL</label><input className={inp} value={e.image_url || ""} onChange={(ev) => set({ image_url: ev.target.value })} placeholder="https://..." data-testid="admin-project-image" /></div>
        <div><label className={lbl}>Description</label><textarea className={`${inp} resize-none`} rows={3} value={e.description || ""} onChange={(ev) => set({ description: ev.target.value })} /></div>
        <div><label className={lbl}>Scope of work</label><textarea className={`${inp} resize-none`} rows={2} value={e.scope || ""} onChange={(ev) => set({ scope: ev.target.value })} /></div>
        <div className="flex gap-6"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={e.featured} onChange={(ev) => set({ featured: ev.target.checked })} className="accent-[#B87333]" data-testid="admin-project-featured" /> Featured</label><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={e.published} onChange={(ev) => set({ published: ev.target.checked })} className="accent-[#B87333]" /> Published</label></div>
      </>)}
    />
  );
}

function PriceGuide() {
  return (
    <CrudSection
      title="Price Item" testid="admin-price" endpoint="/price-guide" adminEndpoint="/admin/price-guide"
      columns={["Category", "Item", "Price"]}
      emptyForm={{ category: "", item: "", unit: "", starting_price: "", price_range: "", scope: "", gst: "", inclusions: "", exclusions: "", quotation_required: true, published: true, sort_order: 0 }}
      renderRow={(it) => (<>
        <td className="px-5 py-3.5 text-sm text-slate-600">{it.category}</td>
        <td className="px-5 py-3.5 text-slate-800 font-medium">{it.item}</td>
        <td className="px-5 py-3.5 text-sm text-slate-600">{it.price_range || it.starting_price || "Quotation"}</td>
      </>)}
      renderForm={(e, set) => (<>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Category</label><input className={inp} value={e.category} onChange={(ev) => set({ category: ev.target.value })} data-testid="admin-price-category" /></div>
          <div><label className={lbl}>Unit</label><input className={inp} value={e.unit || ""} onChange={(ev) => set({ unit: ev.target.value })} placeholder="per sq ft" /></div>
        </div>
        <div><label className={lbl}>Item</label><input className={inp} value={e.item} onChange={(ev) => set({ item: ev.target.value })} data-testid="admin-price-item" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Starting Price</label><input className={inp} value={e.starting_price || ""} onChange={(ev) => set({ starting_price: ev.target.value })} placeholder="$6.00" data-testid="admin-price-starting" /></div>
          <div><label className={lbl}>Price Range</label><input className={inp} value={e.price_range || ""} onChange={(ev) => set({ price_range: ev.target.value })} placeholder="$375 – $570" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>GST status</label><input className={inp} value={e.gst || ""} onChange={(ev) => set({ gst: ev.target.value })} placeholder="Subject to GST" /></div>
          <div><label className={lbl}>Sort order</label><input type="number" className={inp} value={e.sort_order || 0} onChange={(ev) => set({ sort_order: Number(ev.target.value) })} /></div>
        </div>
        <div><label className={lbl}>Scope / Notes</label><input className={inp} value={e.scope || ""} onChange={(ev) => set({ scope: ev.target.value })} /></div>
        <div><label className={lbl}>Inclusions</label><input className={inp} value={e.inclusions || ""} onChange={(ev) => set({ inclusions: ev.target.value })} /></div>
        <div><label className={lbl}>Exclusions</label><input className={inp} value={e.exclusions || ""} onChange={(ev) => set({ exclusions: ev.target.value })} /></div>
        <div className="flex gap-6"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={e.quotation_required} onChange={(ev) => set({ quotation_required: ev.target.checked })} className="accent-[#B87333]" /> Quotation required</label><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={e.published} onChange={(ev) => set({ published: ev.target.checked })} className="accent-[#B87333]" /> Published</label></div>
      </>)}
    />
  );
}

function Testimonials() {
  return (
    <CrudSection
      title="Testimonial" testid="admin-testimonials" endpoint="/testimonials" adminEndpoint="/admin/testimonials"
      columns={["Name", "Rating", "Published"]}
      emptyForm={{ name: "", role: "", content: "", rating: 5, published: false }}
      renderRow={(it) => (<>
        <td className="px-5 py-3.5 text-slate-800 font-medium">{it.name}</td>
        <td className="px-5 py-3.5 text-sm text-slate-600">{it.rating}★</td>
        <td className="px-5 py-3.5 text-sm">{it.published ? "Yes" : "No"}</td>
      </>)}
      renderForm={(e, set) => (<>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Name</label><input className={inp} value={e.name} onChange={(ev) => set({ name: ev.target.value })} data-testid="admin-testimonial-name" /></div>
          <div><label className={lbl}>Role / Project</label><input className={inp} value={e.role || ""} onChange={(ev) => set({ role: ev.target.value })} /></div>
        </div>
        <div><label className={lbl}>Content</label><textarea className={`${inp} resize-none`} rows={4} value={e.content} onChange={(ev) => set({ content: ev.target.value })} data-testid="admin-testimonial-content" /></div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <div><label className={lbl}>Rating</label><select className={inp} value={e.rating} onChange={(ev) => set({ rating: Number(ev.target.value) })}>{[5,4,3,2,1].map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
          <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={e.published} onChange={(ev) => set({ published: ev.target.checked })} className="accent-[#B87333]" data-testid="admin-testimonial-published" /> Published (only genuine)</label>
        </div>
      </>)}
    />
  );
}

function FAQs() {
  return (
    <CrudSection
      title="FAQ" testid="admin-faqs" endpoint="/faqs" adminEndpoint="/admin/faqs"
      columns={["Question", "Category", "Published"]}
      emptyForm={{ question: "", answer: "", category: "General", published: true }}
      renderRow={(it) => (<>
        <td className="px-5 py-3.5 text-slate-800 font-medium max-w-md truncate">{it.question}</td>
        <td className="px-5 py-3.5 text-sm text-slate-600">{it.category}</td>
        <td className="px-5 py-3.5 text-sm">{it.published ? "Yes" : "No"}</td>
      </>)}
      renderForm={(e, set) => (<>
        <div><label className={lbl}>Question</label><input className={inp} value={e.question} onChange={(ev) => set({ question: ev.target.value })} data-testid="admin-faq-question" /></div>
        <div><label className={lbl}>Answer</label><textarea className={`${inp} resize-none`} rows={4} value={e.answer} onChange={(ev) => set({ answer: ev.target.value })} data-testid="admin-faq-answer" /></div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <div><label className={lbl}>Category</label><input className={inp} value={e.category || ""} onChange={(ev) => set({ category: ev.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={e.published} onChange={(ev) => set({ published: ev.target.checked })} className="accent-[#B87333]" /> Published</label>
        </div>
      </>)}
    />
  );
}

function ContactInfo() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/contact-info").then(({ data }) => setForm(data)).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/contact-info", form); toast.success("Contact info updated"); } catch { toast.error("Save failed"); } };
  if (!form) return <Loader2 className="animate-spin text-[#B87333]" />;
  const fields = [["address", "Address"], ["email", "Email"], ["phone", "Phone"], ["whatsapp", "WhatsApp Number (digits, incl. country code)"], ["hdb_license", "HDB License No. (only if verified)"], ["business_hours", "Business Hours"]];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-xl" data-testid="admin-contact">
      <div className="space-y-4">
        {fields.map(([k, label]) => (
          <div key={k}><label className={lbl}>{label}</label><input className={inp} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-contact-${k}`} /></div>
        ))}
      </div>
      <button onClick={save} data-testid="admin-contact-save" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold"><Save size={17} /> Save Changes</button>
    </div>
  );
}
